// PartyPage.js
import React, { useEffect, useMemo, useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { BoxArrowUpRight, ChevronDown, ChevronUp, FileText, Globe } from 'react-bootstrap-icons';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import useApi2Resource from '../../../hooks/useApi2Resource';
import useBallotParties from '../../../hooks/useBallotParties';
import { STATE_OPTIONS, useHomeLocation } from '../../../utils/homeLocation';
import {
  PARTY_KEYS,
  displayFecName,
  fetchParty,
  fetchPartyMoney,
  fetchPartyNews,
  fetchStateParty,
  formatElectionDay,
  formatMoney,
  formatNewsDate,
  formatShare,
  normalizeWebsite,
  officeLabel,
  partyColor,
  partyName,
  partyPath,
  titleCaseFiling,
} from '../../../utils/parties';
import { onActivate, openExternal } from '../../../utils/external';
import PartiesOnBallot from './PartiesOnBallot';
import StanceCompare from './StanceCompare';
import StatePicker from './StatePicker';
import { FeedButton, FeedCard } from '../../feed/FeedCard';

import '../../members/MemberDetailPage.css';
import './Parties.css';

/**
 * PartyPage
 *
 * One party, mounted at /us/party/:partyKey, optionally ?state=VA.
 *
 * Built for the reader who thinks party first: they want to know what their
 * party says, who it is running where they vote, what it is saying this week,
 * and where its money comes from -- and then, often, how the others compare.
 * The page is ordered by that, not by the API's sections:
 *
 *   the other parties on this ballot   one tap across
 *   who the party is                   its own summary line and website
 *   on your ballot                     count, state party, candidates by office
 *   where it stands                    platform passages by topic, comparable
 *   latest                             the party's own posts, one card each
 *   money                              party-wide, national committee, sources
 *   the platform itself                contents and the full document
 *   about                              the longer description, verbatim
 *
 * ─── Built from feed items ────────────────────────────────────────────────────
 *
 * Every block is a FeedCard (components/feed/FeedCard), the same building
 * block the spending and company pages use: the feed's card surface and
 * footer buttons in every theme, without restating them. Sections are a
 * heading above their card, as topics are on the home page, and the party's
 * posts are one card each, as a feed is.
 *
 * ─── State ────────────────────────────────────────────────────────────────────
 *
 * The state is in the query string rather than the path because the page is
 * one party with or without it: /us/party/green is a complete page, and
 * ?state=VA adds a ballot to it. Without the parameter it falls back to the
 * reader's saved state, so a link shared nationally still lands locally.
 *
 * The picker at the top changes it. Like the elections panel, it browses and
 * never writes the saved location, and the URL is replaced rather than pushed
 * so Back leaves the party page instead of stepping through every state
 * looked at. Away from home, a second button goes back to it.
 *
 * ─── Voice ────────────────────────────────────────────────────────────────────
 *
 * Every description, platform passage and news item here is the party's own
 * text, verbatim, from the website its committee filed with the FEC. The page
 * attributes it as such and adds no characterisation of its own. Money is
 * FEC filings. Candidates are the state's certified list where one is loaded.
 */

// CandidateDetailPage's route. Kept here as one line so a route rename is one
// edit rather than a search.
const federalCandidatePath = (candId) => `/us/candidate/${candId}`;

const CANDIDATES_SHOWN = 8;
const CONTENTS_SHOWN = 10;
const ABOUT_PARAGRAPHS_SHOWN = 2;

const LEVEL_ORDER = { federal: 0, state: 1, county: 2, city: 3, local: 4 };
const OFFICE_ORDER = { US_President: 0, US_Senate: 1, US_House: 2 };

const sortBallotRows = (a, b) =>
  (LEVEL_ORDER[a.level] ?? 9) - (LEVEL_ORDER[b.level] ?? 9) ||
  (OFFICE_ORDER[a.office] ?? 9) - (OFFICE_ORDER[b.office] ?? 9) ||
  Number(a.district || 0) - Number(b.district || 0) ||
  String(a.office_as_listed || a.office || '').localeCompare(
    String(b.office_as_listed || b.office || '')
  ) ||
  String(a.candidate_name || '').localeCompare(String(b.candidate_name || ''));

// ─── Page pieces ──────────────────────────────────────────────────────────────

// A heading above its card, as a topic heading sits above its feed.
const Section = ({ title, aside, children }) => (
  <section className="party-section">
    <div className="party-section-head">
      <h2 className="party-section-title">{title}</h2>
      {aside}
    </div>
    {children}
  </section>
);

const ExternalLink = ({ url, children, className = '' }) => {
  if (!url) return null;
  return (
    <span
      className={`party-link ${className}`}
      role="link"
      tabIndex={0}
      onClick={() => openExternal(url)}
      onKeyDown={onActivate(() => openExternal(url))}
    >
      {children}
      <BoxArrowUpRight size={11} className="ms-1" aria-hidden />
    </span>
  );
};

const Stat = ({ value, label }) =>
  value ? (
    <div className="party-stat">
      <div className="party-stat-value">{value}</div>
      <div className="party-stat-label">{label}</div>
    </div>
  ) : null;

const MoreButton = ({ open, onClick, moreLabel, lessLabel }) => (
  <FeedButton onClick={onClick} aria-expanded={open}>
    {open ? <ChevronUp size={12} className="me-1" /> : <ChevronDown size={12} className="me-1" />}
    {open ? lessLabel : moreLabel}
  </FeedButton>
);

// ─── Stance ───────────────────────────────────────────────────────────────────

const StanceItem = ({ topicKey, topic, partyKey, stateAbbr }) => {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const [comparing, setComparing] = useState(false);

  const passage = topic.top_passage || {};
  const sentences = Array.isArray(passage.sentences) ? passage.sentences : [];
  const preview = sentences[0] || '';

  return (
    <div className={`stance ${open ? 'stance--open' : ''}`}>
      <button
        type="button"
        className="stance-toggle"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <span className="stance-toggle-text">
          <span className="stance-label">{topic.label || topicKey}</span>
          {!open && preview && <span className="stance-preview">{preview}</span>}
        </span>
        {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>

      {open && (
        <div className="stance-body">
          {passage.heading && (
            <div className="stance-heading">
              {passage.heading}
              {passage.page ? (
                <span className="party-muted">
                  {' '}
                  {t('common:platformPage', 'page {{page}}', { page: passage.page })}
                </span>
              ) : null}
            </div>
          )}
          {sentences.map((sentence, index) => (
            <p key={index} className="stance-quote">
              {sentence}
            </p>
          ))}
          {topic.passages_found > 1 && (
            <p className="party-muted stance-count">
              {t('common:passagesFound', '{{count}} passages in the platform mention this', {
                count: topic.passages_found,
              })}
            </p>
          )}

          <div className="stance-actions">
            <FeedButton
              aria-expanded={comparing}
              onClick={() => setComparing((current) => !current)}
            >
              {comparing
                ? t('common:hideComparison', 'Hide other parties')
                : t('common:compareParties', 'What the other parties say')}
            </FeedButton>
          </div>

          {comparing && (
            <StanceCompare topic={topicKey} activeKey={partyKey} stateAbbr={stateAbbr} />
          )}
        </div>
      )}
    </div>
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────────

function PartyPage() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { partyKey } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { location: home } = useHomeLocation();

  const key = String(partyKey || '').toLowerCase();
  const requestedState = String(searchParams.get('state') || '').toUpperCase();

  const stateOption = useMemo(
    () =>
      STATE_OPTIONS.find((option) => option.abbr === requestedState) ||
      STATE_OPTIONS.find((option) => option.abbr === home?.stateAbbr) ||
      null,
    [requestedState, home?.stateAbbr]
  );
  const stateAbbr = stateOption?.abbr || '';
  const stateName = stateOption?.name || '';

  const homeAbbr = home?.stateAbbr || '';
  const homeName = STATE_OPTIONS.find((option) => option.abbr === homeAbbr)?.name || homeAbbr;

  const chooseState = (abbr) => {
    const next = new URLSearchParams(searchParams);
    next.set('state', abbr);
    setSearchParams(next, { replace: true });
  };

  const [newsScope, setNewsScope] = useState(stateAbbr ? 'state' : 'national');
  const [showAllCandidates, setShowAllCandidates] = useState(false);
  const [showAllContents, setShowAllContents] = useState(false);
  const [showFullAbout, setShowFullAbout] = useState(false);

  // A new party is a new page: back to the top, and every expander closed.
  useEffect(() => {
    window.scrollTo(0, 0);
    setShowAllCandidates(false);
    setShowAllContents(false);
    setShowFullAbout(false);
  }, [key]);

  useEffect(() => {
    setNewsScope(stateAbbr ? 'state' : 'national');
  }, [key, stateAbbr]);

  const profile = useApi2Resource(() => fetchParty(key), [key]);
  const money = useApi2Resource(() => fetchPartyMoney(key), [key]);
  const stateParty = useApi2Resource(
    stateAbbr ? () => fetchStateParty(key, stateAbbr) : null,
    [key, stateAbbr]
  );
  const ballot = useBallotParties(stateAbbr);
  const news = useApi2Resource(
    () =>
      fetchPartyNews(key, {
        state: newsScope === 'state' ? stateAbbr : undefined,
        nationalOnly: newsScope === 'national',
        limit: 6,
      }),
    [key, newsScope, stateAbbr]
  );

  const color = partyColor(key);

  // ─── Not found / loading ───

  if (profile.loading) {
    return (
      <div id="feedDisplay" className="member-detail-page">
        <div className="member-page-content party-page">
          <div className="party-loading">
            <Spinner id="spinner" animation="grow" variant="light" />
          </div>
        </div>
      </div>
    );
  }

  if (profile.error || !profile.data) {
    const missing = profile.error?.notFound;
    return (
      <div id="feedDisplay" className="member-detail-page">
        <div className="member-page-content party-page">
          <FeedCard>
            <p className="party-lead party-empty">
              {missing
                ? t(
                    'common:partyNotFound',
                    'No party matches that address. These are the parties on file:'
                  )
                : t(
                    'common:partyLoadError',
                    'This party could not be loaded right now. Try again shortly.'
                  )}
            </p>
            {missing && (
              <div className="ballot-parties-row party-empty-list">
                {PARTY_KEYS.map((option) => (
                  <button
                    key={option}
                    type="button"
                    className="ballot-party"
                    style={{ '--party-color': partyColor(option) }}
                    onClick={() => navigate(partyPath(option, stateAbbr))}
                  >
                    <span
                      className="party-badge-dot"
                      style={{ backgroundColor: partyColor(option) }}
                      aria-hidden
                    />
                    <span className="ballot-party-label">{partyName(option)}</span>
                  </button>
                ))}
              </div>
            )}
          </FeedCard>
        </div>
      </div>
    );
  }

  const party = profile.data;
  const name = party.name || partyName(key);
  const summary = party.about?.summary_line || party.in_its_own_words || null;
  const site = normalizeWebsite(party.national_committee?.website);
  const platforms = Array.isArray(party.platforms) ? party.platforms : [];
  const primaryPlatform = platforms[0] || null;

  // ─── Ballot ───

  const ballotData = ballot.data;
  const ballotRow = ballotData?.parties?.find((row) => row.party_key === key) || null;
  const ballotDay = formatElectionDay(ballotData?.date);
  const running = (ballotData?.candidates || [])
    .filter((row) => row.party_key === key)
    .sort(sortBallotRows);
  const runningShown = showAllCandidates ? running : running.slice(0, CANDIDATES_SHOWN);

  const committee = stateParty.data;
  const committeeSite = normalizeWebsite(committee?.website);

  // ─── Stances ───

  const topicEntries = Object.entries(party.stances?.topics || {});
  const addressed = topicEntries.filter(([, topic]) => topic?.addressed && topic.top_passage);
  const unaddressed = topicEntries.filter(([, topic]) => topic && topic.addressed === false);

  // ─── Money ───

  const moneyData = money.data;
  const national = moneyData?.national_committee;
  const sources = Array.isArray(national?.where_it_came_from) ? national.where_it_came_from : [];

  // ─── About ───

  const aboutText = party.about?.in_its_own_words || '';
  const aboutParagraphs = aboutText
    .split(/\n\s*\n/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);
  const aboutShown = showFullAbout
    ? aboutParagraphs
    : aboutParagraphs.slice(0, ABOUT_PARAGRAPHS_SHOWN);
  const aboutSource = normalizeWebsite(party.about?.source_url);

  const contents = Array.isArray(primaryPlatform?.contents) ? primaryPlatform.contents : [];
  const contentsShown = showAllContents ? contents : contents.slice(0, CONTENTS_SHOWN);

  const newsItems = Array.isArray(news.data?.items) ? news.data.items : [];

  return (
    <div id="feedDisplay" className="member-detail-page">
      <div className="member-page-content party-page" style={{ '--party-color': color }}>
        {/* ─── Where ─── */}
        <div className="party-toolbar">
          <StatePicker
            value={stateAbbr}
            onChange={chooseState}
            placeholder={t('common:chooseState', 'Choose a state')}
          />
          {homeAbbr && stateAbbr && stateAbbr !== homeAbbr && (
            <div className="feed-item">
              <Button
                className="clickable party-home-button"
                id="viewBillButton"
                onClick={() => chooseState(homeAbbr)}
              >
                {t('common:backToMyState', 'Back to {{state}}', { state: homeName })}
              </Button>
            </div>
          )}
        </div>

        {stateAbbr && (
          <PartiesOnBallot
            key={stateAbbr}
            stateAbbr={stateAbbr}
            activeKey={key}
            className="party-switcher"
          />
        )}

        {/* ─── Who ─── */}
        <FeedCard
          className="party-hero"
          title={name}
          footer={
            (site || primaryPlatform?.url) && (
              <>
                {site && (
                  <FeedButton primary onClick={() => openExternal(site.url)}>
                    <Globe size={13} className="me-1" aria-hidden />
                    {site.host}
                  </FeedButton>
                )}
                {primaryPlatform?.url && (
                  <FeedButton onClick={() => openExternal(primaryPlatform.url)}>
                    <FileText size={13} className="me-1" aria-hidden />
                    {t('common:platform', 'Platform')}
                  </FeedButton>
                )}
              </>
            )
          }
        >
          {summary && (
            <figure className="party-summary">
              <blockquote>{summary}</blockquote>
              {site && (
                <figcaption className="party-muted">
                  {t('common:inItsOwnWordsFrom', 'In its own words, from {{site}}', {
                    site: site.host,
                  })}
                </figcaption>
              )}
            </figure>
          )}
        </FeedCard>

        {/* ─── On your ballot ─── */}
        {stateAbbr ? (
          <Section title={t('common:partyInState', 'In {{state}}', { state: stateName })}>
            <FeedCard
              footer={
                <>
                  <FeedButton primary onClick={() => navigate(`/us/candidates/${stateAbbr}`)}>
                    {t('common:allStateCandidates', 'All {{state}} candidates', {
                      state: stateAbbr,
                    })}
                  </FeedButton>
                  {running.length > CANDIDATES_SHOWN && (
                    <MoreButton
                      open={showAllCandidates}
                      onClick={() => setShowAllCandidates((current) => !current)}
                      moreLabel={t('common:showAllCount', 'Show all {{count}}', {
                        count: running.length,
                      })}
                      lessLabel={t('common:showFewer', 'Show fewer')}
                    />
                  )}
                </>
              }
            >
              {ballot.loading && (
                <div className="party-inline-loading">
                  <Spinner animation="border" size="sm" />
                </div>
              )}

              {!ballot.loading && ballotDay && (
                <p className="party-lead">
                  {ballotRow
                    ? t(
                        'common:partyOnBallotCount',
                        '{{count}} {{party}} candidates on the {{date}} ballot.',
                        { count: ballotRow.candidates, party: ballotRow.label, date: ballotDay }
                      )
                    : t(
                        'common:partyNotOnBallot',
                        'No {{party}} candidates are on the {{date}} ballot in {{state}}.',
                        { party: name, date: ballotDay, state: stateName }
                      )}
                </p>
              )}

              {committee && (
                <div className="party-committee">
                  <div className="party-committee-name">{titleCaseFiling(committee.name)}</div>
                  <div className="party-committee-meta">
                    {committeeSite && committee.site_reachable !== false && (
                      <ExternalLink url={committeeSite.url}>{committeeSite.host}</ExternalLink>
                    )}
                    {formatMoney(committee.money?.raised) && (
                      <span className="party-muted">
                        {t('common:raisedThisCycle', 'Raised {{amount}} this cycle', {
                          amount: formatMoney(committee.money.raised),
                        })}
                      </span>
                    )}
                  </div>
                </div>
              )}

              {running.length > 0 && (
                <ul className="party-candidates">
                  {runningShown.map((row, index) => {
                    const linkable = row.level === 'federal' && row.cand_id;
                    const office = String(row.office || '').startsWith('US_')
                      ? officeLabel(row.office, row.district, t)
                      : [
                          row.office_as_listed || row.office,
                          row.district &&
                            t('common:districtLabel', 'District {{district}}', {
                              district: row.district,
                            }),
                        ]
                          .filter(Boolean)
                          .join(', ');
                    const raised = formatMoney(row.finance?.raised);
                    const open = () => navigate(federalCandidatePath(row.cand_id));

                    return (
                      <li
                        key={`${row.cand_id || row.candidate_name}-${row.office}-${row.district || ''}-${index}`}
                        className={`party-candidate ${linkable ? 'party-candidate--link' : ''}`}
                        role={linkable ? 'link' : undefined}
                        tabIndex={linkable ? 0 : undefined}
                        onClick={linkable ? open : undefined}
                        onKeyDown={linkable ? onActivate(open) : undefined}
                      >
                        <div className="party-candidate-main">
                          <div className="party-candidate-name">
                            {displayFecName(row.candidate_name)}
                            {row.incumbent_challenger === 'incumbent' && (
                              <span className="party-tag">{t('common:incumbent', 'Incumbent')}</span>
                            )}
                          </div>
                          <div className="party-muted">{office}</div>
                        </div>
                        {raised && (
                          <div className="party-candidate-money">
                            <div>{raised}</div>
                            <div className="party-muted">{t('common:raised', 'raised')}</div>
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </FeedCard>
          </Section>
        ) : (
          <Section title={t('common:onYourBallot', 'On your ballot')}>
            <FeedCard>
              <p className="party-lead">
                {t(
                  'common:partyChooseState',
                  'Choose a state above to see this party\u2019s candidates, state party and news there.'
                )}
              </p>
            </FeedCard>
          </Section>
        )}

        {/* ─── Where it stands ─── */}
        {topicEntries.length > 0 && (
          <Section title={t('common:whereItStands', 'Where it stands')}>
            <FeedCard>
              <p className="party-muted party-section-note">
                {t(
                  'common:stancesNote',
                  'Passages from its platform, in its own words, picked by the same rule for every party.'
                )}
              </p>

              <div className="stances">
                {addressed.map(([topicKey, topic]) => (
                  <StanceItem
                    key={`${key}-${topicKey}`}
                    topicKey={topicKey}
                    topic={topic}
                    partyKey={key}
                    stateAbbr={stateAbbr}
                  />
                ))}
              </div>

              {unaddressed.length > 0 && (
                <p className="party-muted stance-unaddressed">
                  {t('common:notInPlatform', 'Not mentioned in its platform: {{topics}}', {
                    topics: unaddressed
                      .map(([topicKey, topic]) => topic.label || topicKey)
                      .join(', '),
                  })}
                </p>
              )}
            </FeedCard>
          </Section>
        )}

        {/* ─── Latest ─── */}
        {/* One card per post, as a feed is: the title opens it, the meta row
            says who published it and when, and the footer carries Source. */}
        <Section
          title={t('common:latestFromParty', 'Latest from the party')}
          aside={
            stateAbbr ? (
              <div className="party-segments feed-item" role="tablist">
                {['state', 'national'].map((scope) => (
                  <Button
                    key={scope}
                    type="button"
                    role="tab"
                    aria-selected={newsScope === scope}
                    className={`clickable party-segment ${
                      newsScope === scope ? 'party-segment--active' : ''
                    }`}
                    onClick={() => setNewsScope(scope)}
                  >
                    {scope === 'state' ? stateAbbr : t('common:national', 'National')}
                  </Button>
                ))}
              </div>
            ) : null
          }
        >
          {news.loading && (
            <div className="party-inline-loading">
              <Spinner animation="border" size="sm" />
            </div>
          )}

          {!news.loading &&
            newsItems.map((item) => {
              const summaryText =
                item.summary && !item.summary.startsWith(item.title) ? item.summary : null;
              const meta = [titleCaseFiling(item.from), formatNewsDate(item.published)]
                .filter(Boolean)
                .join(', ');

              return (
                <FeedCard
                  key={item.url}
                  className="party-news-card"
                  title={item.title}
                  onTitleClick={() => openExternal(item.url)}
                  footer={
                    <FeedButton primary onClick={() => openExternal(item.url)}>
                      <BoxArrowUpRight size={12} className="me-1" aria-hidden />
                      {t('common:source', 'Source')}
                    </FeedButton>
                  }
                >
                  {summaryText && <div className="party-news-summary">{summaryText}</div>}
                  {meta && <div className="party-news-meta">{meta}</div>}
                </FeedCard>
              );
            })}

          {!news.loading && !newsItems.length && (
            <FeedCard
              footer={
                newsScope === 'state' ? (
                  <FeedButton primary onClick={() => setNewsScope('national')}>
                    {t('common:seeNationalNews', 'See national news')}
                  </FeedButton>
                ) : null
              }
            >
              <p className="party-lead">
                {newsScope === 'state'
                  ? t(
                      'common:noStatePartyNews',
                      'Nothing from the {{state}} party\u2019s own feed yet.',
                      { state: stateName }
                    )
                  : t(
                      'common:noPartyNews',
                      'This party publishes no news feed on its filed website.'
                    )}
              </p>
            </FeedCard>
          )}
        </Section>

        {/* ─── Money ─── */}
        {moneyData && (
          <Section title={t('common:partyMoney', 'Money')}>
            <FeedCard>
              {moneyData.headline && (
                <p className="party-lead">{moneyData.headline.replace(/\.?$/, '.')}</p>
              )}

              {national?.money && (
                <>
                  <div className="party-muted party-subhead">{titleCaseFiling(national.name)}</div>
                  <div className="party-stats">
                    <Stat
                      value={formatMoney(national.money.raised)}
                      label={t('common:raised', 'raised')}
                    />
                    <Stat
                      value={formatMoney(national.money.spent)}
                      label={t('common:spent', 'spent')}
                    />
                    <Stat
                      value={formatMoney(national.money.cash_on_hand)}
                      label={t('common:cashOnHand', 'cash on hand')}
                    />
                  </div>
                </>
              )}

              {sources.length > 0 && (
                <div className="party-sources">
                  <div className="party-muted party-subhead">
                    {t(
                      'common:whereItCameFrom',
                      'Where the national committee\u2019s money came from'
                    )}
                  </div>
                  {sources.map((source) => (
                    <div key={source.source} className="party-source">
                      <div className="party-source-row">
                        <span className="party-source-label">{source.label}</span>
                        <span className="party-source-share">{formatShare(source.share)}</span>
                      </div>
                      <div className="party-source-track" aria-hidden>
                        <span
                          className="party-source-fill"
                          style={{
                            width: `${Math.max(1, Math.round(Number(source.share) * 100))}%`,
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {national?.money?.through && (
                <p className="party-muted party-footnote">
                  {t(
                    'common:partyMoneyFootnote',
                    'FEC filings through {{date}}. Party-wide totals count money moved between the party\u2019s own committees once.',
                    { date: national.money.through }
                  )}
                </p>
              )}
            </FeedCard>
          </Section>
        )}

        {/* ─── Platform ─── */}
        <Section title={t('common:platform', 'Platform')}>
          <FeedCard
            footer={
              primaryPlatform ? (
                <>
                  <FeedButton primary onClick={() => openExternal(primaryPlatform.url)}>
                    <FileText size={13} className="me-1" aria-hidden />
                    {t('common:readFullPlatform', 'Read the full platform')}
                  </FeedButton>
                  {contents.length > CONTENTS_SHOWN && (
                    <MoreButton
                      open={showAllContents}
                      onClick={() => setShowAllContents((current) => !current)}
                      moreLabel={t('common:allSections', 'All {{count}} sections', {
                        count: contents.length,
                      })}
                      lessLabel={t('common:showFewer', 'Show fewer')}
                    />
                  )}
                </>
              ) : null
            }
          >
            {primaryPlatform ? (
              <>
                <p className="party-lead">
                  {t('common:platformWords', '{{title}}, {{words}} words.', {
                    title: primaryPlatform.title || t('common:thePlatform', 'The platform'),
                    words: Number(primaryPlatform.words || 0).toLocaleString(),
                  })}
                </p>

                {contentsShown.length > 0 && (
                  <ol className="party-contents">
                    {contentsShown.map((heading, index) => (
                      <li key={`${heading}-${index}`}>{titleCaseFiling(heading)}</li>
                    ))}
                  </ol>
                )}
              </>
            ) : (
              <p className="party-lead">
                {t(
                  'common:noPlatformFound',
                  'No platform was found on the party\u2019s filed website.'
                )}
              </p>
            )}
          </FeedCard>
        </Section>

        {/* ─── About ─── */}
        {aboutParagraphs.length > 0 && (
          <Section title={t('common:aboutParty', 'About')}>
            <FeedCard
              footer={
                aboutParagraphs.length > ABOUT_PARAGRAPHS_SHOWN || aboutSource ? (
                  <>
                    {aboutSource && (
                      <FeedButton primary onClick={() => openExternal(party.about.source_url)}>
                        <BoxArrowUpRight size={12} className="me-1" aria-hidden />
                        {t('common:source', 'Source')}
                      </FeedButton>
                    )}
                    {aboutParagraphs.length > ABOUT_PARAGRAPHS_SHOWN && (
                      <MoreButton
                        open={showFullAbout}
                        onClick={() => setShowFullAbout((current) => !current)}
                        moreLabel={t('common:readMore', 'Read more')}
                        lessLabel={t('common:showLess', 'Show less')}
                      />
                    )}
                  </>
                ) : null
              }
            >
              <div className="party-about">
                {aboutShown.map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
              </div>
            </FeedCard>
          </Section>
        )}

        <p className="party-muted party-provenance">
          {t(
            'common:partyProvenance',
            'Descriptions, platforms and news are the party\u2019s own words, from the website its committee filed with the FEC. Money is from FEC filings. Candidates are the state\u2019s published list where it has one.'
          )}
        </p>
      </div>
    </div>
  );
}

export default PartyPage;